/**
 *  <gh-view-editor> — drag-and-drop view editor
 *
 *  Renders a view in edit mode with drag-and-drop container management.
 *  Use <gh-view> for read-only form rendering.
 *
 *  ATTRIBUTES:
 *  'app-id'    - Application ID to load views from
 *  'view-id'   - ID of the specific view to edit; edits first view if omitted
 *  'decorator' - Two-way binding to a view model object (overrides app-id/view-id lookup)
 *
 *  VIEW MODEL SHAPE ('model' / 'decorator'):
 *      'template_id' - Template ID for block identification in 'views_list'
 *      'name'        - Display name of the container
 *      'icon_id'     - Icon identifier for the container
 *      'class'       - CSS class(es) applied to the container
 *      'priority'    - Sort order within parent containers
 *      'content'     - Array of child container/element descriptors
 *
 *  CONTAINER TEMPLATES (defined per container module, stored via viewTemplatesStorage):
 *      'allowed_types'      - Which template_ids can be dropped into this container
 *      'html_editor'        - HTML template used to render each container in edit mode
 *      'container_settings' - Toolbar button descriptors for the container:
 *          'name'    - Button label
 *          'icon'    - Button icon
 *          'function'- Scope function name to call on click
 *          'arg'     - Argument passed to the function
 *          'show_if' - Show button only if the container has this CSS class
 *          'hide_if' - Hide button if the container has this CSS class
 **/

import { contextMenuHandler } from './gh_view_editor_context_menu.js';
import * as Element from './views/element_editor.js';
import * as Flex from './views/flex_editor.js';
import * as Float from './views/float_editor.js';
import * as Tabs from './views/tabs_editor.js';
import * as Dropdown from './views/dropdown_editor.js';

let Templates = [Element, Flex, Float, Tabs, Dropdown];

angular.module('GhTemplateEditorModule', [])

.directive("ghViewEditor", ['$animate', function($animate) {
    let templatePatternDefault = `{
        'allowed_types': ['gh_float', 'gh_dropdown'],
        'template_id': 'gh_flex',
        'name': '',
        'icon_id': '',
        'class': 'gh_flex gh_col_height_0 gh_height_percent vertical_scroll',
        'container_id': 0,
        'priority': 0,
        'content': [{
            'allowed_types': ['gh_float', 'gh_element', 'gh_tabs', 'gh_dropdown'],
            'template_id': 'gh_float',
            'name': '',
            'icon_id': '',
            'class': 'gh_float gh_col_width_12 gh_col_height_0 gh_height_percent gh_width_percent vertical_scroll',
            'container_id': 0,
            'priority': 0,
            'content': []
        }]
    }`;

    let directive = {
        template:
            `<div class="ghViewContainer ghEditor" ng-repeat="list in views = [model]">
                <div class="ghViewNavigation">
                    <span class="ghViewAdd" gh-icon="plus_in_circle 86BEF6 26px normal" ng-click="list.content.unshift(${ templatePatternDefault })"></span>
                </div>
                <ul class="ghViewContainerList" ng-init="lists = list.content"
                dnd-list="lists"
                dnd-allowed-types="['gh_flex']"
                dnd-drop="dropElement(index, item, external, type, lists)" dnd-inserted="insertedElement()">

                    <li class="ghViewContainerListItem" ng-class="item.class" ng-repeat="item in lists | orderBy:'priority'"
                    dnd-draggable="item"
                    dnd-effect-allowed="move"
                    dnd-moved="$parent.lists.splice($index, 1)"
                    dnd-type="item.template_id"
                    ng-include="item.template_id + '_editor'"></li>
                </ul>
            </div>`,
        replace: false,
        restrict: 'E',
        controller: controller,
        scope: {
            appId: '@',
            viewId: '@',
            decorator: '=?'
        }
    };

    controller.$inject = ['$scope', 'formEditorService', 'GhDialog', '$element', 'PipeService', 'viewTemplatesStorage', 'cnfg', '$location', '$route', 'GhElementEditor', 'GhElementEditorLight', 'Message', 'localizationService'];

    async function controller($scope, formEditorService, GhDialog, $element, PipeService, viewTemplatesStorage, cnfg, $location, $route, GhElementEditor, GhElementEditorLight, Message, localizationService) {
        $scope.templates = viewTemplatesStorage.getContainersTemplates();

        $animate.enabled(false, $element);

        // Dropdown menus are position:fixed so they escape clipping by any
        // horizontally-scrolling ancestor; their on-screen coordinates have
        // to be computed in JS since CSS alone can't read an element's
        // own position relative to the viewport.
        $scope.positionDropdownMenu = function(event) {
            const trigger = event.currentTarget;
            const menu = trigger.querySelector('.dropdown_elements');
            if (!menu) { return; }

            const rect = trigger.getBoundingClientRect();
            menu.style.setProperty('--dropdown-menu-top', rect.bottom + 'px');
            menu.style.setProperty('--dropdown-menu-left', rect.left + 'px');
        };

        $scope.setIcon = function(item) {
            GhDialog.show({
                position: 'bottom',
                template: {
                    content: '<div class="gh-ui-select-icon-list">' +
                        '<div class="gh-ui-select-icon" ng-click="update(icon.id)" ng-repeat="icon in iconList" gh-icon="{{icon.id}} a0a7ad 90% normal" ng-class="{\'--selected\': icon.id == currentIcon}"></div>' +
                        '</div>'
                },
                locals: {
                    currentIcon: item.icon_id
                },
                controller: ['$scope', 'iconsStorage', 'currentIcon', function($scope, iconsStorage, currentIcon) {
                    $scope.currentIcon = currentIcon;
                    $scope.iconList = iconsStorage.getIconsList('000000');

                    $scope.update = function(iconId) {
                        $scope.hide(iconId);
                    };
                }]
            }).then(function(data) {
                item.icon_id = data || item.icon_id;
            });
        };

        //-----------------------------------------------

        $scope.model = {};

        function update(newVal) {
            $scope.model = getView(newVal);
        }

        function getView(data) {
            let view = {};

            if ($scope.viewId) {
                angular.forEach(data, function(value) {
                    if (value.view_id == $scope.viewId) view = value;
                });
            } else {
                view = data[0];
            }

            return view;
        }

        let address = {
            app_id: $scope.appId
        };

        if ($scope.appId) {
            const views_list = await gudhub.appProcessor.getAppViews($scope.appId);
            update(views_list);
            PipeService.on('gh_app_views_update', address, viewsUpdatePipe);
        }

        function viewsUpdatePipe(_event, data) {
            update(data);
        }

        const handleContextMenuEditor = contextMenuHandler($scope, { cnfg, $location, $route, formEditorService, GhElementEditor, GhElementEditorLight, Message, PipeService, $element, localizationService });
        window.addEventListener('contextmenu', handleContextMenuEditor);

        $scope.$on('$destroy', function() {
            console.log('DESTROY');
            PipeService.destroy('gh_app_views_update', address, viewsUpdatePipe);
            window.removeEventListener('contextmenu', handleContextMenuEditor);
        });

        $scope.$watch('decorator', function(newDecorator) {
            if (newDecorator) {
                $scope.model = newDecorator;
            }
        }, true);

        //-----------------------------------------------

        $scope.saveModelElement = {};
        $scope.getElementModelT = function(element_id) {
            if (element_id) {
                angular.copy(formEditorService.getElementModel(element_id), $scope.saveModelElement);

                return $scope.saveModelElement;
            }
        };

        $scope.dropElement = function(index, items, external, type, lists) {
            formEditorService.dropElement();
            if (items.length) {
                for (let i = 0; i < items.length; i++) {
                    lists.splice(index + i, 0, items[i]);
                    formEditorService.addElementToExisting(items[i].data_model);
                }
            } else {
                return items;
            }
        };

        // Tabs are rendered in array order, but priority is what the rest of the
        // view pipeline sorts by, so keep it in step after a drag-and-drop reorder.
        $scope.syncTabsPriority = function(tabs) {
            tabs.forEach((tab, index) => { tab.priority = index; });
        };

        $scope.insertedElement = function() {
            formEditorService.AddAllowedTypes($scope.model);
        };
    }

    return directive;
}])


/*======================================================================================================|
|======================================   FIELD TEMPLATES STORAGE   ====================================|
|=======================================================================================================|
|-- here we store all fields templates
*/
.service('viewTemplatesStorage', ['$templateCache', function($templateCache) {

    /* ------------------------------- LOAD TEMPLATES AND CACHE HTML ------------------------------- */
    /*-- Here we load and cache containers "Templates" for gh-view */
    (function loadTemplates() {
        angular.forEach(Templates, function(value) {
            $templateCache.put(value.default.model.template_id + '_editor', value.default.html);
        });
    }());

    /*-- Return "Templates" list */
    this.getContainersTemplates = function() {
        return Templates;
    };

    this.getAllowedTypes = function(currentTemplateName, parent) {
        let templates = this.getContainersTemplates();
        for (let i = 0; i < templates.length; i++) {
            if (templates[i].default.model.template_id == currentTemplateName) {
                if (parent.content.length) {
                    for (let k = 0; k < parent.content.length; k++) {
                        if (parent.content[k].template_id == 'gh_float') {
                            return ['gh_float'];
                        } else {
                            return templates[i].default.allowed_types;
                        }
                    }
                }
            }
        }
    };
}]);
